import React, { useEffect, useState } from 'react'
import './RefundPolicy.css'
import Header3 from './components/Header3'
import Header from './components/Header'
import Header2 from './components/Header2'
import Header5 from './components/Header5'

const FEES = [
  ['8+ days', 'Flat R10', 'PENDING', 'Refund request submitted successfully.'],
  ['4–7 days', 'Flat R20', 'PENDING', 'Refund request submitted successfully.'],
  ['1–3 days', '25% + R15', 'PENDING', 'Refund request submitted successfully.'],
  ['Same-day', 'Not permitted', 'DECLINED', 'Refunds are not allowed on the day of the event.'],
  ['Scanned tickets', 'Not applicable', 'DECLINED', 'Ticket already used and not refundable.'],
]

const NAV = [
  { id: 'eligibility', title: 'Eligibility overview' },
  { id: 'fees', title: 'Refund fee structure' },
  { id: 'timeline', title: 'Processing timeline' },
  { id: 'exceptions', title: 'Exceptions and overrides' },
  { id: 'support', title: 'Support' },
]

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0])
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
          setActive(visible[0].target.id)
        }
      },
      { rootMargin: '-15% 0px -70% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) obs.observe(el)
    })
    return () => obs.disconnect()
  }, [ids])
  return active
}

function RefundPolicy() {
  useEffect(() => {
    document.title = 'Karamale Refund Policy'
  }, [])

  const active = useActiveSection(NAV.map((n) => n.id))

  return (
    <>
      {localStorage.getItem('customername') !== null ? (
        <Header2 />
      ) : localStorage.getItem('businessname') !== null ? (
        <Header3 />
      ) : localStorage.getItem('partner') !== null ? (
        <Header5 />
      ) : (
        <Header />
      )}

      <main className="rp">
        <div className="rp-wrap">
          <header className="rp-hero">
            <h1>Karamale Refund Policy</h1>
            <p>
              At Karamale, we strive to balance fairness and operational feasibility in every refund
              decision. Our structured refund policy ensures transparency for ticket buyers while
              safeguarding event organisers from last-minute cancellations and attendance
              manipulation.
            </p>
          </header>

          <div className="rp-body">
            <nav className="rp-nav" aria-label="Refund policy sections">
              <ul>
                {NAV.map(({ id, title }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={active === id ? 'is-active' : ''}
                      aria-current={active === id ? 'true' : undefined}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="rp-content">
              <section id="eligibility" className="rp-section">
                <h2>
                  <span className="rp-num">1.</span>Eligibility overview
                </h2>
                <ul>
                  <li>Refunds can be requested up to the day before the event.</li>
                  <li>
                    Tickets scanned at the venue are considered used and are not eligible for
                    refunds.
                  </li>
                  <li>A valid refund reason is required with each request.</li>
                </ul>
              </section>

              <section id="fees" className="rp-section">
                <h2>
                  <span className="rp-num">2.</span>Refund fee structure
                </h2>
                <table className="rp-table">
                  <thead>
                    <tr>
                      <th>Time before event</th>
                      <th>Refund fee</th>
                      <th>Status</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FEES.map(([time, fee, status, details]) => (
                      <tr key={time}>
                        <td data-label="Time before event">
                          <strong>{time}</strong>
                        </td>
                        <td data-label="Refund fee">{fee}</td>
                        <td data-label="Status">
                          <span className={`rp-pill rp-pill--${status.toLowerCase()}`}>
                            {status}
                          </span>
                        </td>
                        <td data-label="Details">{details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="rp-fineprint">
                  * Refund amounts are calculated after fees and cannot exceed the original ticket
                  price.
                </p>
              </section>

              <section id="timeline" className="rp-section">
                <h2>
                  <span className="rp-num">3.</span>Processing timeline
                </h2>
                <ul>
                  <li>
                    Refunds are reviewed within <strong>5 business days</strong>.
                  </li>
                  <li>
                    Approved refunds are paid within <strong>7–10 working days</strong>.
                  </li>
                </ul>
              </section>

              <section id="exceptions" className="rp-section">
                <h2>
                  <span className="rp-num">4.</span>Exceptions and overrides
                </h2>
                <p>
                  Event organisers may manually override refund restrictions for exceptional
                  circumstances, such as medical emergencies or service interruptions. These
                  overrides are securely logged and subject to audit.
                </p>
              </section>

              <section id="support" className="rp-section">
                <h2>
                  <span className="rp-num">5.</span>Support
                </h2>
                <p>
                  For any refund-related inquiries or disputes, please contact our support team with
                  your ticket ID and refund reason. We’re here to help resolve issues fairly and
                  efficiently.
                </p>
              </section>
            </article>
          </div>
        </div>
      </main>
    </>
  )
}

export default RefundPolicy
